import React, { useState, useEffect } from 'react';
import {
Drawer,
AppBar,
Toolbar,
IconButton,
List,
ListItem,
ListItemIcon,
ListItemText,
Collapse,
Divider,
Typography,
Avatar,
Menu,
MenuItem,
useMediaQuery,
useTheme
} from '@mui/material';
import {
Menu as MenuIcon,
Close as CloseIcon,
Dashboard as DashboardIcon,
TrackChanges as TrackIcon,
Autorenew as RenewalIcon,
AccountBalanceWallet as ExpensesIcon,
FitnessCenter as GymIcon,
Receipt as OtherExpensesIcon,
Group as CustomersIcon,
Today as DailyIcon,
DateRange as MonthlyIcon,
AdminPanelSettings as AdminIcon,
People as StaffIcon,
ExitToApp as LogoutIcon,
Work as WorkIcon,
ExpandMore as ExpandMoreIcon,
ExpandLess as ExpandLessIcon,
ChevronLeft as ChevronLeftIcon,
ChevronRight as ChevronRightIcon,
// Marketing icons
Campaign as MarketingIcon,
Email as EmailIcon,
LocalOffer as PriceIcon,
Person as UserIcon,
// Commodities / LPO icons
Inventory2 as CommoditiesIcon,
Category as CommodityIcon,
AssignmentInd as RequisitionerIcon
} from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BallotIcon from '@mui/icons-material/Ballot';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import useCustomToast from '../hooks/useToast';
import userIcon from "../img/admin-1.png";
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../utilities/Permission';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
width: open ? drawerWidth : collapsedDrawerWidth,
flexShrink: 0,
whiteSpace: 'nowrap',
boxSizing: 'border-box',
'& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedDrawerWidth,
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
    }),
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
},
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
'&:hover': {
    backgroundColor: theme.palette.primary.light,
},
borderRadius: theme.shape.borderRadius,
margin: theme.spacing(0.5, 1),
paddingLeft: theme.spacing(2),
paddingRight: theme.spacing(2),
minHeight: 48,
justifyContent: 'initial',
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
minWidth: 0,
marginRight: theme.spacing(open => open ? 2 : 'auto'),
justifyContent: 'center',
color: 'inherit',
}));

const NavBar = () => {
const showToast = useCustomToast();
const [mobileOpen, setMobileOpen] = useState(false);
const [desktopOpen, setDesktopOpen] = useState(true);
const [commoditiesOpen, setCommoditiesOpen] = useState(false); // State for LPO dropdown
const [emailsOpen, setEmailsOpen] = useState(false);
const [messageOpen, setMessageOpen] = useState(false);
const [careersOpen, setCareersOpen] = useState(false);
const [applicationOpen, setApplicationOpen] = useState(false);
const [systemUsersOpen, setSystemUsersOpen] = useState(false);
const [reportsOpen, setReportsOpen] = useState(false);
const [anchorEl, setAnchorEl] = useState(null);
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('md'));
const navigate = useNavigate();
const location = useLocation();
const userType = 'Admin';
const [userState, setUserState] = useState({
    name: '',
    contact: '',
    role: '',
    id: ''
});

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

// Helper function
const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    loadUser();
}, []);

const loadUser = () => {
    const user = sessionStorage.getItem('userInfo');
    if (user) {
    const jsonData = JSON.parse(user);
    setUserState(prev => ({
        ...prev,
        name: jsonData.name,
        contact: jsonData.contact,
        id: jsonData.id,
        role: jsonData.role
    }));
    }
};

const handleDrawerToggle = () => {
    if (isMobile) {
    setMobileOpen(!mobileOpen);
    } else {
    setDesktopOpen(!desktopOpen);
    }
};

const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
};

const handleMenuClose = () => {
    setAnchorEl(null);
};

const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
    setMobileOpen(false);
    }
};

const handleLogOut = () => {
    sessionStorage.removeItem('userInfo');
    handleNavigation('/');
    showToast("Warning", "You have logged out successfully", "warning");
};

const drawerContent = (
    <div>
    <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        minHeight: 64 
    }}>
        {desktopOpen && (
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Maila Group
        </Typography>
        )}
        <IconButton onClick={handleDrawerToggle} color="inherit">
        {isMobile ? (
            <CloseIcon />
        ) : desktopOpen ? (
            <ChevronLeftIcon />
        ) : (
            <ChevronRightIcon />
        )}
        </IconButton>
    </Toolbar>
    <Divider sx={{ bgcolor: '#FFFFFF' }} />

    <List>
        {/* LPO / Commodities Section */}
        <>
        <StyledListItem 
            button 
            onClick={() => desktopOpen && setCommoditiesOpen(!commoditiesOpen)}
            sx={{ justifyContent: desktopOpen ? 'initial' : 'center' }}
        >
            <StyledListItemIcon open={desktopOpen} sx={{ paddingRight: 2 }}>
            <CommoditiesIcon />
            </StyledListItemIcon>
            {desktopOpen && (
            <>
                <ListItemText primary="LPO" />
                {commoditiesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </>
            )}
        </StyledListItem>
        {desktopOpen && (
            <Collapse in={commoditiesOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/lpo-orders')}
                selected={location.pathname === '/lpo-orders'}
            >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <AssignmentIcon />
                </ListItemIcon>
                <ListItemText primary="Orders" />
            </StyledListItem>
            <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/lpo-2-orders')}
                selected={location.pathname === '/lpo-2-orders'}
            >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <AssignmentIcon />
                </ListItemIcon>
                <ListItemText primary="Orders II" />
            </StyledListItem>
                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/commodity')}
                selected={location.pathname === '/commodity'}
                >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <CommodityIcon />
                </ListItemIcon>
                <ListItemText primary="Commodity" />
                </StyledListItem>

                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/vendor')}
                selected={location.pathname === '/vendor'}
                >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <RequisitionerIcon />
                </ListItemIcon>
                <ListItemText primary="Vendor" />
                </StyledListItem>

                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/handler')}
                selected={location.pathname === '/handler'}
                >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <RequisitionerIcon />
                </ListItemIcon>
                <ListItemText primary="Handler" />
                </StyledListItem>
            </List>
            </Collapse>
        )}
        </>

        {/* Messages Section */}
        <StyledListItem
        button
        onClick={() => desktopOpen && setMessageOpen(!messageOpen)}
        sx={{ justifyContent: desktopOpen ? 'initial' : 'center' }}
        >
        <StyledListItemIcon open={desktopOpen} sx={{ paddingRight: 2 }}>
            <CustomersIcon />
        </StyledListItemIcon>
        {desktopOpen && (
            <>
            <ListItemText primary="Messages" />
            {messageOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </>
        )}
        </StyledListItem>
        {desktopOpen && (
        <Collapse in={messageOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
            <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/contact-us')}
                selected={location.pathname === '/contact-us'}
            >
                <ListItemIcon sx={{ color: 'inherit' }}>
                <DailyIcon />
                </ListItemIcon>
                <ListItemText primary="Contact Us" />
            </StyledListItem>
            </List>
        </Collapse>
        )}

        {/* Marketing Section */}
        <>
        <StyledListItem 
            button 
            onClick={() => desktopOpen && setEmailsOpen(!emailsOpen)}
            sx={{ justifyContent: desktopOpen ? 'initial' : 'center' }}
        >
            <StyledListItemIcon open={desktopOpen} sx={{ paddingRight: 2 }}>
            <MarketingIcon />
            </StyledListItemIcon>
            {desktopOpen && (
            <>
                <ListItemText primary="Marketing" />
                {emailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </>
            )}
        </StyledListItem>
        {desktopOpen && (
            <Collapse in={emailsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/prices')}
                selected={location.pathname === '/prices'}
                >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <PriceIcon />
                </ListItemIcon>
                <ListItemText primary="Prices" />
                </StyledListItem>

                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/customer-emails')}
                selected={location.pathname === '/customer-emails'}
                >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <EmailIcon />
                </ListItemIcon>
                <ListItemText primary="Emails" />
                </StyledListItem>
            </List>
            </Collapse>
        )}
        </>

        {/* Careers Section */}
        <>
        <StyledListItem 
            button 
            onClick={() => desktopOpen && setCareersOpen(!careersOpen)}
            sx={{ justifyContent: desktopOpen ? 'initial' : 'center' }}
        >
            <StyledListItemIcon open={desktopOpen} sx={{ paddingRight: 2 }}>
            <WorkIcon />
            </StyledListItemIcon>
            {desktopOpen && (
            <>
                <ListItemText primary="Careers" />
                {careersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </>
            )}
        </StyledListItem>
        {desktopOpen && (
            <Collapse in={careersOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/jobs')}
                selected={location.pathname === '/jobs'}
                >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <BallotIcon />
                </ListItemIcon>
                <ListItemText primary="Jobs" />
                </StyledListItem>
                <StyledListItem
                button
                sx={{ pl: 4 }}
                onClick={() => handleNavigation('/applications')}
                selected={location.pathname === '/applications'}
                >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <AssignmentIcon />
                </ListItemIcon>
                <ListItemText primary="Applications" />
                </StyledListItem>
            </List>
            </Collapse>
        )}
        </>

        {/* System Users Section */}
        {hasPermission(PERMISSIONS.ALL) && (
        <>
            <StyledListItem 
            button 
            onClick={() => desktopOpen && setSystemUsersOpen(!systemUsersOpen)}
            sx={{ justifyContent: desktopOpen ? 'initial' : 'center' }}
            >
            <StyledListItemIcon open={desktopOpen} sx={{ paddingRight: 2 }}>
                <AdminIcon />
            </StyledListItemIcon>
            {desktopOpen && (
                <>
                <ListItemText primary="System Users" />
                {systemUsersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </>
            )}
            </StyledListItem>
            {desktopOpen && (
            <Collapse in={systemUsersOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                <StyledListItem
                    button
                    sx={{ pl: 4 }}
                    onClick={() => handleNavigation('/users')}
                    selected={location.pathname === '/users'}
                >
                    <ListItemIcon sx={{ color: 'inherit' }}>
                    <UserIcon />
                    </ListItemIcon>
                    <ListItemText primary="Users" />
                </StyledListItem>
                </List>
            </Collapse>
            )}
        </>
        )}
    </List>
    </div>
);

return (
    <>
    <AppBar
        position="fixed"
        sx={{
        width: { md: `calc(100% - ${desktopOpen ? drawerWidth : collapsedDrawerWidth}px)` },
        ml: { md: `${desktopOpen ? drawerWidth : collapsedDrawerWidth}px` },
        backgroundColor: 'background.blue',
        color: 'text.primary',
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        }}
    >
        <Toolbar>
        <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
        >
            <MenuIcon />
        </IconButton>

        <div style={{ flexGrow: 1 }} />

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}
        >
            <Typography variant="body1" sx={{ display: { xs: 'none', sm: 'block', color: 'white' } }}>
            {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })}
            </Typography>

            <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{ ml: 2 }}
            aria-controls="account-menu"
            aria-haspopup="true"
            >
            <img src={userIcon} style={{ width: 32, height: 32 }} alt='user' />
            </IconButton>

            <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
                elevation: 0,
                sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                },
                '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                },
                },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
            <center>
                <p>{userState.name}</p>
                <p>{userState.email}</p>
                <p>{userState.role}</p>
            </center>
            <Divider />
            <MenuItem onClick={() => handleLogOut()}>
                <ListItemIcon>
                <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
            </MenuItem>
            </Menu>
        </motion.div>
        </Toolbar>
    </AppBar>

    {/* Mobile Drawer */}
    <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
        keepMounted: true,
        }}
        sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
        }}
    >
        {drawerContent}
    </Drawer>

    {/* Desktop Drawer */}
    <StyledDrawer
        variant="permanent"
        open={desktopOpen}
        sx={{
        display: { xs: 'none', md: 'block' },
        }}
    >
        {drawerContent}
    </StyledDrawer>
    </>
);
};

export default NavBar;